/// <reference types="@types/googlemaps" />

import { Component, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AgmMap } from '@agm/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';

import { Environment } from '../../core/environment';
import { BreakpointsService } from '../../core/services/breakpoints.service';
import { PdfService } from '../../core/pdf-client/pdf.service';
import { PrintFormType } from '../../core/pdf-client/pdf-request.interface';
import {
  FindNearestLocationsByCoordinatesGQL,
  LocationType,
  ProductName
} from '../../core/graphql-client/graphql';
import {
  IvfDialogComponent,
  TreatmentDialogComponent,
  PharmacyDialogComponent
} from '../../core/dialogs/dialogs';
import { EmailDialogComponent } from '../../core/dialogs/email-dialog/email-dialog.component';
import { EmailSuccessComponent } from '../../core/dialogs/email-success/email-success.component';

@Component({
  selector: 'finders-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss']
})
export class MapComponent implements OnInit {
  latitude: string;
  longitude: string;
  locationAddress: string;
  locationAddressInput: string;
  locationType: string;
  // All results from server
  results: any;
  animationTriggers: any;
  // Visible results from server
  visibleResults: any;
  // URL to send as an email - not currently used
  url: any;
  // Are we searching for a result currently? If so the map is hidden
  searching: boolean;
  // hideMap is used to destroy the map on mobile
  hideMap: boolean;
  breakpointsSubscription: Subscription;
  page = 1;
  true = true;
  marker: string;

  listStr: string[];

  @ViewChild('AgmMap', { static: false }) mapElement: AgmMap;

  constructor(
    private findNearestLocationsByCoordinatesGQL: FindNearestLocationsByCoordinatesGQL,
    private breakpoint: BreakpointsService,
    private dialog: MatDialog,
    private pdf: PdfService,
    private route: ActivatedRoute,
    private router: Router,
    public env: Environment
  ) {
    this.hideMap = false;
    this.results = [];
    this.visibleResults = [];
    this.searching = false;
    this.url = encodeURIComponent(window.location.href);
  }

  ngOnInit() {
    this.breakpointsSubscription = this.breakpoint
      .getBreakpoints()
      .subscribe(message => {
        this.hideMap = message.isMobile;
      });

    // If we're linking directly to the map use this
    this.route.params.subscribe(params => {
      this.results = [];
      this.visibleResults = [];
      this.searching = true;
      this.latitude = params['latitude'];
      this.longitude = params['longitude'];
      this.locationType = params['type'];
      this.locationAddressInput = params['input'];
      this.locationAddress = params['address'];
      this.page = params['page'] || 1;

      if (
        this.latitude === undefined ||
        this.longitude === undefined ||
        this.locationType === undefined
      ) {
        this.router.navigate(['']);
      } else {
        if (this.env.product === ProductName.Evenity) {
          this.marker = '/assets/images/svgs/map-marker';
        } else {
          if (this.locationType === LocationType.Treatment) {
            this.marker = '/assets/images/svgs/map-marker-green';
          } else {
            this.marker = '/assets/images/svgs/map-marker-blue';
          }
        }

        this.findNearestLocationsByCoordinates();
      }
    });
  }

  private trimDistance(distance) {
    const trimmed = distance.substring(0, 4);
    if (trimmed.endsWith('.')) {
      return trimmed.substring(0, 3);
    } else {
      return trimmed;
    }
  }

  public findNearestLocationsByCoordinates() {
    this.results = [];
    this.visibleResults = [];

    this.findNearestLocationsByCoordinatesGQL
      .watch({
        longitude: this.longitude,
        latitude: this.latitude,
        locationType: <LocationType>this.locationType,
        // productName: ProductName.Prolia,
        productName: this.env.product,
        limitLocations: 100,
        limitDistance: 300
      })
      .valueChanges.subscribe(({ data }) => {
        let counter = 1;

        for (const loc of data.findNearestLocationsByCoordinates) {
          this.results.push({
            ...loc,
            distance: this.trimDistance(loc.distance),
            label: (counter++).toString(),
            active: false,
            formsOpen: false,
            selected: false,
            locationType: this.locationType
          });
        }

        const visibleResultsLength =
          this.results.length >= 10 ? 10 : this.results.length;

        this.searching = false;
        return this.results;
      });
  }

  public markerActive(result) {
    for (const res of this.visibleResults) {
      res.selected = false;
    }

    result.selected = true;
  }

  public markerInactive(result) {
    result.selected = false;
  }

  public locationTypeChanged(locationType) {
    this.locationType = locationType;
    this.findNearestLocationsByCoordinates();
  }

  public recenterMap() {
    this.mapElement.triggerResize(true);
  }

  printResults() {
    window.print();
  }

  public toggleForms(result) {
    if (result.formsOpen === true) {
      result.formsOpen = false;
    } else {
      result.formsOpen = true;
    }
  }

  openTreatmentDialog(result) {
    const config = new MatDialogConfig();
    config.data = result;
    this.dialog.open(TreatmentDialogComponent, config);
  }

  openivfDialog(result) {
    const config = new MatDialogConfig();
    config.data = result;
    this.dialog.open(IvfDialogComponent, config);
  }

  openPharmacyDialog(result) {
    const config = new MatDialogConfig();
    config.data = result;
    this.dialog.open(PharmacyDialogComponent, config);
  }

  openEmailDialog() {
    const config = new MatDialogConfig();
    config.minWidth = '50vw';
    config.data = {
      locations: this.visibleResults.map(location => {
        return {
          name: location.name,
          address1: location.address1,
          address2: location.address2 || '',
          city: location.city,
          state: location.state,
          zip: location.zipCode,
          phone: location.phone,
          distance: location.distance
        };
      })
    };
    this.dialog
      .open(EmailDialogComponent, config)
      .afterClosed()
      .subscribe(result => {
        if (result !== 'cancel') {
          let config = new MatDialogConfig();
          config.data = {
            email: result
          };
          this.dialog.open(EmailSuccessComponent, config);
        }
      });
  }

  public openPPForm(result) {
    const pdfRequest = {
      npi: '',
      speciality: '',
      physicianTaxID: '',
      siteName: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zipCode: '',
      phone: '',
      fax: '',
      officeContact: '',
      physicianName: '',
      enumPrintFormType: PrintFormType.PP,
      locationType: <LocationType>this.locationType,
      productName: this.env.product,
      locationQL: {
        name: result.name,
        address1: result.address1,
        address2: result.address2,
        city: result.city,
        state: result.state,
        zipCode: result.zipCode,
        phone: result.phone,
        fax: result.fax
      }
    };

    this.pdf.populatePdf(
      pdfRequest,
      `${this.env.product} Patient Printout.pdf`
    );
  }

  public mapReady() {
    const visibleResultsLength =
      this.results.length >= 10 ? 10 : this.results.length;

    if (this.page === 1) {
      this.visibleResults = this.results.slice(0, visibleResultsLength);
    } else {
      this.visibleResults = this.results.slice((this.page-1)*10, ((this.page-1)*10) + 10);
      //console.log(this.results.slice(20, visibleResultsLength + 10));
    }

    this.markerActive(this.visibleResults[0]);
  }

  public loadNextResults() {
    this.router.navigate([
      'results',
      {
        latitude: this.latitude,
        longitude: this.longitude,
        address: this.locationAddress,
        type: this.locationType,
        input: this.locationAddress,
        page: ++this.page
      }
    ]);
  }

  public loadPrevResults() {
    this.router.navigate([
      'results',
      {
        latitude: this.latitude,
        longitude: this.longitude,
        address: this.locationAddress,
        type: this.locationType,
        input: this.locationAddress,
        page: --this.page
      }
    ]);
  }

  public mapCenterChange(latLng) {
    // console.log(latLng);
  }
}
